import { useAuthContext } from "../../auth/state/AuthContext";
import { CircleSendIcon, DislikeIcon, LikeIcon, ShipCircleIcon } from "../../shared/Icons";
import { hasUWHCMessageAtom, themeAtom, userAtom } from "../../shared/atoms";
import { log, logE, logV } from "../../shared/services/debug-logger";
import {
  AssistantFeedback,
  AssistantIconRenderProps,
  ChatMessageWithKhRepoData,
  ChatMsg,
  OtHullCleaningChatProps,
} from "../models/uwhc.models";
import { openAssistantSocket } from "../services/uwhc.service";
import {
  buildMessageWithInitialContext,
  buildWsEnvelope,
  formatMessageTime,
  getAssistantQuestionError,
  normalizeApi,
  unwrapWsEnvelope,
} from "../utils/uwhc.utils";
import { ASSISTANT_CHAT_LIMITS, DEFAULT_ASSISTANT_CONFIG, FEEDBACK_REASONS } from "../uwhc.constants";
import KhRepoKnowledgeExpandableText from "./KhRepoKnowledgeExpandableText";
import { UwhcProvidersTable } from "./UwhcProvidersTable";
import { datadogRum } from "@datadog/browser-rum";
import { useOktaAuth } from "@okta/okta-react";
import { useAtomValue, useSetAtom } from "jotai";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { twMerge } from "tailwind-merge";

// Shows a conversation for one assistant through the authenticated OpsLink relay.
// A new connection starts a new upstream conversation; old messages and feedback IDs are cleared before retrying.
function OtHullCleaningChat({
  workflow = "uwhc",
  placeholder = "Ask about underwater hull cleaning...",
  className,
  mainClassName,
  mainTrackClassName,
  onClose,
  assistantConfig,
  responseNormalizer = normalizeApi,
  initialUserMessageContext,
}: OtHullCleaningChatProps) {
  const [messages, setMessages] = useState<ChatMessageWithKhRepoData[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [connectionAttempt, setConnectionAttempt] = useState(0);
  const [lastQuestionTs, setLastQuestionTs] = useState<number | null>(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackTargetTs, setFeedbackTargetTs] = useState<number | null>(null);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [feedbackDetails, setFeedbackDetails] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lastQuestionRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const pendingAssistantTsRef = useRef<number | null>(null);
  const hasSentUserMessageRef = useRef(false);
  const { sub: _email } = useAuthContext().state.user?.accessToken || {};
  const email = (_email ?? "").toLowerCase();
  const { authState } = useOktaAuth();
  const isAuthenticated = authState?.isAuthenticated === true;
  const signedInSubject = authState?.accessToken?.claims.sub;

  const user = useAtomValue(userAtom);
  const theme = useAtomValue(themeAtom);
  const mergedAssistantConfig = {
    ...DEFAULT_ASSISTANT_CONFIG,
    ...assistantConfig,
    quickPrompts: assistantConfig?.quickPrompts ?? DEFAULT_ASSISTANT_CONFIG.quickPrompts,
  };
  const chatUi = {
    ...theme.uwhcAssistant.chatUi,
    ...(assistantConfig?.uiOverrides ?? {}),
  };
  const setHasMessage = useSetAtom(hasUWHCMessageAtom);
  const initialSystemWelcome = mergedAssistantConfig.systemWelcome;
  const renderAssistantIcon =
    mergedAssistantConfig.renderAssistantIcon ??
    ((props: AssistantIconRenderProps) => <ShipCircleIcon className={props.className} color={props.color} />);
  const renderCloseIcon =
    mergedAssistantConfig.renderCloseIcon ??
    ((props: { className?: string }) => (
      <svg
        className={props.className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    ));

  useEffect(() => {
    if (lastQuestionRef.current && lastQuestionTs !== null) {
      lastQuestionRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, [messages.length, lastQuestionTs]);

  // A new connection owns fresh conversation and feedback identifiers, even when the same user reconnects.
  const resetChatForNewConnection = useCallback(() => {
    log("STATE - resetChatForNewConnection");
    setConnected(false);
    setBusy(false);
    setMessages([]);
    setInput("");
    setHasMessage(false);
    setLastQuestionTs(null);
    setShowFeedbackModal(false);
    setFeedbackTargetTs(null);
    setSelectedReason(null);
    setFeedbackDetails("");
    pendingAssistantTsRef.current = null;
    hasSentUserMessageRef.current = false;
  }, [setHasMessage]);

  useEffect(() => {
    resetChatForNewConnection();

    if (!isAuthenticated || !signedInSubject) {
      log("OtHullCleaningChat - INIT - sign in required");
      setErr("Sign in to use the assistant.");
      return;
    }

    let socket: WebSocket;

    try {
      socket = openAssistantSocket(workflow);
    } catch (connectionError) {
      logE(new Error("OtHullCleaningChat - INIT - connection failed"));
      setErr(connectionError instanceof Error ? connectionError.message : "Unable to connect to the assistant.");
      return;
    }

    wsRef.current = socket;
    logV("OtHullCleaningChat - SUB assistant", { workflow });
    setErr(null);
    const connectionTimeout = window.setTimeout(() => {
      logE(new Error("OtHullCleaningChat - SUB-E assistant - connection timeout"));
      setErr("The assistant is taking too long to connect. Please try again.");
      socket.close();
    }, ASSISTANT_CHAT_LIMITS.connectionTimeoutMs);

    socket.onopen = () => {
      log("OtHullCleaningChat - SUB-E assistant - connected", { workflow });
      window.clearTimeout(connectionTimeout);
      setConnected(true);
    };

    socket.onmessage = (event) => {
      let eventJson: any;
      try {
        eventJson = JSON.parse(event.data);
      } catch {
        logE(new Error("OtHullCleaningChat - SUB-E assistant - invalid response"));
        setErr("The assistant returned an unreadable response. Start a new chat to try again.");
        socket.close(4002, "Invalid assistant response");
        return;
      }

      const { message_type, payload } = unwrapWsEnvelope(eventJson);

      if (message_type === "relay_error") {
        logE(new Error("OtHullCleaningChat - SUB-E assistant - relay failed"));
        setErr(typeof payload?.message === "string" ? payload.message : "The assistant request failed.");
        setConnected(false);
        setBusy(false);
        pendingAssistantTsRef.current = null;
        socket.close();
        return;
      }

      const infoPayload = message_type === "info_message" ? payload : payload?.info_message;
      if (infoPayload !== undefined) {
        return;
      }

      const normalizedPayload = payload ?? {};
      const normalizedResponse = responseNormalizer(normalizedPayload);
      const assistantMessage: ChatMessageWithKhRepoData = {
        role: "assistant",
        text: normalizedResponse.text,
        data: normalizedResponse.data,
        execution_id: normalizedPayload.execution_id,
        message_id: normalizedPayload.message_id,
        feedback: null,
        ts: Date.now(),
      };

      setMessages((previous) => [...previous, assistantMessage]);
      log("STATE - applyAssistantMessage", { workflow });
      pendingAssistantTsRef.current = null;
      setBusy(false);
      setErr(null);
      inputRef.current?.focus();
    };

    socket.onerror = () => {
      logE(new Error("OtHullCleaningChat - SUB-E assistant - connection error"));
      // Browser error events contain the token-bearing socket URL; keep them out of telemetry.
      setErr((previous) => previous ?? "Unable to connect to the assistant. Please try again.");
    };

    socket.onclose = (event) => {
      log("OtHullCleaningChat - SUB-E assistant - closed", { code: event.code });
      window.clearTimeout(connectionTimeout);
      setConnected(false);
      setShowFeedbackModal(false);
      setErr(
        (previous) =>
          previous ??
          (event.code === 4001
            ? "Your chat session has expired. Start a new chat to reconnect."
            : "The assistant connection closed. Start a new chat to reconnect."),
      );
      if (pendingAssistantTsRef.current !== null) {
        setMessages((previous) => [
          ...previous,
          {
            role: "assistant",
            text: "Sorry-there was an error reaching the service.",
            data: null,
            feedback: null,
            ts: Date.now(),
          },
        ]);
        pendingAssistantTsRef.current = null;
      }
      setBusy(false);
    };

    return () => {
      logV("OtHullCleaningChat - DISPOSE", { workflow });
      window.clearTimeout(connectionTimeout);
      if (wsRef.current === socket) {
        wsRef.current = null;
      }
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    };
  }, [connectionAttempt, isAuthenticated, resetChatForNewConnection, responseNormalizer, signedInSubject, workflow]);

  useEffect(() => {
    if (!connected || initialSystemWelcome.trim().length === 0) {
      return;
    }

    setMessages((previous) =>
      previous.some((message) => message.isInitialWelcome)
        ? previous
        : [
            ...previous,
            {
              role: "system",
              text: initialSystemWelcome,
              data: null,
              execution_id: undefined,
              message_id: undefined,
              feedback: null,
              isInitialWelcome: true,
              ts: Date.now(),
            },
          ],
    );
  }, [connected, initialSystemWelcome]);

  const sendFeedback = (payload: AssistantFeedback): boolean => {
    if (!payload.message_id || wsRef.current?.readyState !== WebSocket.OPEN) {
      setErr("Feedback was not sent because this chat is disconnected.");
      return false;
    }

    if ((payload.details?.length ?? 0) > ASSISTANT_CHAT_LIMITS.feedbackCharacters) {
      setErr("Feedback can contain up to 4,000 characters. Shorten your feedback and try again.");
      return false;
    }

    try {
      wsRef.current.send(buildWsEnvelope("feedback", payload));
      log("OtHullCleaningChat - sendFeedback - sent");
      setErr(null);
      return true;
    } catch {
      // Native socket errors may contain the URL and its access token.
      logE(new Error("OtHullCleaningChat - sendFeedback - failed"));
      setErr("Feedback could not be sent. Please try again.");
      return false;
    }
  };

  const setMessageFeedback = (targetTs: number, feedback: "like" | "dislike" | null) => {
    setMessages((previous) =>
      previous.map((msg) =>
        msg.ts === targetTs
          ? {
              ...msg,
              feedback,
            }
          : msg,
      ),
    );
  };

  const handleLike = (message: ChatMsg) => {
    if (message.feedback === "like") {
      setMessageFeedback(message.ts, null);
      return;
    }

    const sent = sendFeedback({
      execution_id: message.execution_id,
      message_id: message.message_id,
      rating: "like",
      source: "button",
    });

    if (sent) {
      setMessageFeedback(message.ts, "like");
    }
  };

  const handleDislike = (message: ChatMsg) => {
    if (message.feedback === "dislike") {
      setMessageFeedback(message.ts, null);
      setShowFeedbackModal(false);
      setFeedbackTargetTs(null);
      setSelectedReason(null);
      setFeedbackDetails("");
      return;
    }

    const sent = sendFeedback({
      execution_id: message.execution_id,
      message_id: message.message_id,
      rating: "dislike",
      source: "button",
    });

    if (!sent) {
      return;
    }

    setMessageFeedback(message.ts, "dislike");
    setFeedbackTargetTs(message.ts);
    setSelectedReason(null);
    setFeedbackDetails("");
    setShowFeedbackModal(true);
  };

  const submitDislikeFeedback = () => {
    if (!selectedReason || feedbackTargetTs === null) {
      return;
    }

    const feedbackMessage = messages.find((msg) => msg.ts === feedbackTargetTs);
    if (!feedbackMessage?.message_id) {
      setShowFeedbackModal(false);
      setFeedbackTargetTs(null);
      setSelectedReason(null);
      setFeedbackDetails("");
      return;
    }

    const details = feedbackDetails.trim();
    const sent = sendFeedback({
      execution_id: feedbackMessage.execution_id,
      message_id: feedbackMessage.message_id,
      rating: "dislike",
      reason: selectedReason,
      details: details.length > 0 ? details : undefined,
      source: "form",
    });

    if (!sent) {
      return;
    }

    setShowFeedbackModal(false);
    setFeedbackTargetTs(null);
    setSelectedReason(null);
    setFeedbackDetails("");
  };

  function askQuestion() {
    const question = input.trim();

    if (!question || busy || pendingAssistantTsRef.current !== null) {
      return;
    }

    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      log("OtHullCleaningChat - askQuestion - disconnected");
      setErr("Chat is not connected yet. Please wait and try again.");
      return;
    }

    const outboundMessage = hasSentUserMessageRef.current
      ? question
      : buildMessageWithInitialContext(question, initialUserMessageContext);
    const envelope = buildWsEnvelope("user_message", { message: outboundMessage, save_message: true });
    const questionError = getAssistantQuestionError(outboundMessage, envelope);

    if (questionError) {
      log("OtHullCleaningChat - askQuestion - validation rejected");
      setErr(questionError);
      return;
    }

    log("OtHullCleaningChat - askQuestion", { workflow });
    setErr(null);
    setBusy(true);

    const userMsg: ChatMsg = {
      role: "user",
      text: question,
      ts: Date.now(),
    };
    pendingAssistantTsRef.current = userMsg.ts + 1;

    setLastQuestionTs(userMsg.ts);
    setHasMessage(true);
    setMessages((existing) => [...existing, userMsg]);
    setInput("");

    try {
      datadogRum.addAction("sendUWHCGenAIRequest", {
        ext_user_email: email,
        submittedBy: user?.name ?? "",
      });

      wsRef.current.send(envelope);
      hasSentUserMessageRef.current = true;
    } catch {
      // Keep native errors out of both the page and telemetry because they may contain the login token.
      logE(new Error("OtHullCleaningChat - askQuestion - failed"));
      setErr("Your question could not be sent. Please try again.");
      setInput(question);
      setMessages((existing) => [
        ...existing,
        {
          role: "assistant",
          text: "Sorry-there was an error reaching the service.",
          data: null,
          feedback: null,
          ts: Date.now(),
        },
      ]);
      pendingAssistantTsRef.current = null;
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      askQuestion();
    }
  }

  function applyPrompt(prompt: string) {
    setInput(prompt);
    inputRef.current?.focus();
  }

  function startNewChat() {
    log("OtHullCleaningChat - startNewChat");
    setConnectionAttempt((previous) => previous + 1);
  }

  const answeredMessagesCount = messages.filter((m) => m.role === "assistant" && m.text !== "...").length;
  logV("OtHullCleaningChat - BUILD", { count: messages.length, connected, busy });

  return (
    <div
      data-testid="assistant-chat"
      className={twMerge(
        "flex h-full min-h-0 flex-col overflow-hidden rounded-[24px] border shadow-[0_28px_80px_rgba(15,23,42,0.18)]",
        chatUi.panelBorder,
        chatUi.panelBg,
        className,
      )}
    >
      <header className={twMerge("border-b px-5 py-3", chatUi.headerBg, chatUi.panelBorder)}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div
              className={twMerge(
                "flex h-10 w-10 items-center justify-center rounded-full",
                chatUi.brandIconBg,
                chatUi.brandIconShadow,
              )}
            >
              {renderAssistantIcon({ className: "h-5 w-5 text-white", color: chatUi.assistantAvatarColor })}
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className={twMerge("text-lg font-semibold tracking-tight", chatUi.titleText)}>
                  {mergedAssistantConfig.title}
                </h1>
                {mergedAssistantConfig.badgeLabel ? (
                  <span className={twMerge("rounded-full px-2.5 py-1 text-[11px] font-semibold", chatUi.brandTag)}>
                    {mergedAssistantConfig.badgeLabel}
                  </span>
                ) : null}
              </div>
              {mergedAssistantConfig.subtitle ? (
                <p className={twMerge("mt-1 text-xs", chatUi.subtitleText)}>{mergedAssistantConfig.subtitle}</p>
              ) : null}
              {mergedAssistantConfig.contextLabel ? (
                <p className={twMerge("mt-1 text-xs", chatUi.subtitleText)}>{mergedAssistantConfig.contextLabel}</p>
              ) : null}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {mergedAssistantConfig.showConnectionStatus ? (
              <span
                className={twMerge(
                  "rounded-full border px-3 py-1 text-xs font-semibold",
                  connected ? chatUi.connectionConnected : chatUi.connectionConnecting,
                )}
              >
                {connected ? "Connected" : err ? "Disconnected" : "Connecting..."}
              </span>
            ) : null}
            {mergedAssistantConfig.showResponseCount ? (
              <span
                className={twMerge(
                  "rounded-full border px-3 py-1 text-xs font-medium",
                  busy ? chatUi.responseBusy : chatUi.responseIdle,
                )}
              >
                {busy ? "Searching" : `${answeredMessagesCount} responses`}
              </span>
            ) : null}
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className={twMerge(
                  "flex h-6 w-6 items-center justify-center rounded-full border transition",
                  chatUi.closeButton,
                  chatUi.closeButtonHover,
                )}
                aria-label={mergedAssistantConfig.closeButtonLabel}
                title={mergedAssistantConfig.closeButtonLabel}
              >
                {renderCloseIcon({ className: "h-3.5 w-3.5" })}
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <main
          className={twMerge(
            "min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar]:w-2",
            chatUi.mainBg,
            chatUi.mainScrollbar,
            mainTrackClassName,
            mainClassName,
          )}
        >
          {messages.length > 0 ? (
            <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-4">
              {messages.map((m, i) => {
                const isLastQuestion = m.role === "user" && m.ts === lastQuestionTs;
                const isFeedbackAllowed = connected && m.role === "assistant" && !!m.message_id && m.text !== "...";
                const isUser = m.role === "user";
                const isSystem = m.role === "system";
                const isWelcomeSystemMessage = isSystem && m.isInitialWelcome === true;
                const useAssistantPresentation = !isUser && (!isSystem || isWelcomeSystemMessage);

                return (
                  <div
                    key={m.ts + ":" + i}
                    className={twMerge("flex w-full", isUser ? "justify-end" : "justify-start")}
                    ref={isLastQuestion ? lastQuestionRef : null}
                  >
                    <div className={twMerge("max-w-[95%] lg:max-w-[92%]", isUser ? "items-end" : "items-start")}>
                      {!isUser ? (
                        <div className="mb-2 flex items-center gap-2">
                          <div
                            className={twMerge(
                              "flex h-6 w-6 items-center justify-center rounded-full",
                              isSystem && !isWelcomeSystemMessage ? chatUi.systemAvatarBg : chatUi.assistantAvatarBg,
                            )}
                          >
                            {renderAssistantIcon({
                              className: "h-4 w-4",
                              color:
                                isSystem && !isWelcomeSystemMessage
                                  ? chatUi.systemAvatarColor
                                  : chatUi.assistantAvatarColor,
                            })}
                          </div>
                          <span className={twMerge("text-xs font-medium", chatUi.assistantMeta)}>
                            {isSystem && !isWelcomeSystemMessage ? "System" : mergedAssistantConfig.assistantLabel}
                          </span>
                        </div>
                      ) : null}

                      <div
                        className={twMerge(
                          "overflow-hidden rounded-[18px] border px-4 py-3 shadow-sm",
                          isUser
                            ? chatUi.userBubble
                            : useAssistantPresentation
                              ? chatUi.assistantBubble
                              : chatUi.systemBubble,
                        )}
                      >
                        {isUser ? (
                          <p
                            className={twMerge(
                              "mb-1 text-[10px] font-semibold uppercase tracking-[0.18em]",
                              chatUi.userEyebrow,
                            )}
                          >
                            You
                          </p>
                        ) : null}

                        <div
                          className={twMerge(
                            "whitespace-pre-wrap text-xs leading-5",
                            theme.home.tailwind.textDefault,
                            isUser ? chatUi.markdownUser : chatUi.markdownAssistant,
                          )}
                        >
                          <ReactMarkdown>{m.text}</ReactMarkdown>
                        </div>

                        {m.role === "assistant" && m.data?.rows && m.data.rows.length > 0 ? (
                          <div className={twMerge("mt-4 rounded-[16px] border p-2", chatUi.tableWrap)}>
                            <UwhcProvidersTable rows={m.data.rows} />
                          </div>
                        ) : null}

                        {m.role === "assistant" && m.data?.rawKnowledgeItems && m.data.rawKnowledgeItems.length > 0 ? (
                          <div className="mt-4">
                            <KhRepoKnowledgeExpandableText
                              rawContent={m.data.rawKnowledgeItems}
                              variant="card"
                              title="Raw Knowledge Sources"
                            />
                          </div>
                        ) : null}

                        {isFeedbackAllowed ? (
                          <div className={twMerge("mt-3 flex items-center gap-2", chatUi.assistantMeta)}>
                            <button
                              data-testid="assistant-chat-like"
                              type="button"
                              className={twMerge(
                                "inline-flex h-8 w-8 items-center justify-center rounded-full border transition",
                                chatUi.feedbackButtonHover,
                                m.feedback === "like" ? chatUi.feedbackButtonActive : chatUi.feedbackButtonIdle,
                              )}
                              onClick={() => handleLike(m)}
                              title="Like response"
                              aria-label="Like response"
                            >
                              <LikeIcon className="h-4 w-4" />
                            </button>
                            <button
                              data-testid="assistant-chat-dislike"
                              type="button"
                              className={twMerge(
                                "inline-flex h-8 w-8 items-center justify-center rounded-full border transition",
                                chatUi.feedbackButtonHover,
                                m.feedback === "dislike" ? chatUi.feedbackButtonActive : chatUi.feedbackButtonIdle,
                              )}
                              onClick={() => handleDislike(m)}
                              title="Dislike response"
                              aria-label="Dislike response"
                            >
                              <DislikeIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : null}
                      </div>

                      <p className={twMerge("mt-1 text-[11px]", chatUi.timestamp, isUser ? "text-right" : "text-left")}>
                        {formatMessageTime(m.ts)}
                      </p>
                    </div>
                  </div>
                );
              })}

              {busy ? (
                <div className="flex w-full justify-start">
                  <div className="max-w-[95%] lg:max-w-[92%]">
                    <div className="mb-2 flex items-center gap-2">
                      <div
                        className={twMerge(
                          "flex h-6 w-6 items-center justify-center rounded-full",
                          chatUi.assistantAvatarBg,
                        )}
                      >
                        {renderAssistantIcon({ className: "h-4 w-4", color: chatUi.assistantAvatarColor })}
                      </div>
                      <span className={twMerge("text-xs font-medium", chatUi.assistantMeta)}>
                        {mergedAssistantConfig.assistantLabel}
                      </span>
                    </div>

                    <div
                      className={twMerge(
                        "overflow-hidden rounded-[18px] border px-4 py-3 shadow-sm",
                        chatUi.typingBubble,
                      )}
                    >
                      <div className="flex items-center gap-1.5">
                        <span
                          className={twMerge(
                            "h-1.5 w-1.5 animate-pulse rounded-full [animation-delay:-0.2s]",
                            chatUi.typingDot,
                          )}
                        />
                        <span
                          className={twMerge(
                            "h-1.5 w-1.5 animate-pulse rounded-full [animation-delay:-0.1s]",
                            chatUi.typingDot,
                          )}
                        />
                        <span className={twMerge("h-1.5 w-1.5 animate-pulse rounded-full", chatUi.typingDot)} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </main>

        {answeredMessagesCount === 0 && mergedAssistantConfig.quickPrompts.length > 0 ? (
          <div className={twMerge("border-t px-5 py-2", chatUi.suggestedBorder, chatUi.suggestedBg)}>
            <div className="flex flex-wrap gap-2">
              {mergedAssistantConfig.quickPrompts.map((quickPrompt) => {
                const prompt = typeof quickPrompt === "string" ? quickPrompt : quickPrompt.prompt;
                const label = typeof quickPrompt === "string" ? quickPrompt : quickPrompt.label;

                return (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => applyPrompt(prompt)}
                    className={twMerge(
                      "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                      chatUi.suggestedChip,
                      chatUi.suggestedChipHover,
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <footer className={twMerge("border-t px-5 pb-2 pt-3", chatUi.sectionBorder, chatUi.footerBg)}>
          {err ? (
            <div
              role="alert"
              data-testid="assistant-chat-error"
              className={twMerge("mb-3 rounded-2xl border px-4 py-3 text-sm", chatUi.errorBox)}
            >
              <p>{err}</p>
              {!connected && isAuthenticated ? (
                <button
                  type="button"
                  data-testid="assistant-chat-start-new"
                  onClick={startNewChat}
                  className="mt-2 font-semibold underline"
                >
                  Start new chat
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="flex gap-3">
            <div className="flex-1">
              <input
                data-testid="assistant-chat-message"
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                className={twMerge(
                  "h-10 w-full rounded-[16px] border px-4 text-[14px] outline-none transition",
                  chatUi.input,
                  chatUi.inputFocus,
                  theme.uwhcAssistant.text,
                )}
                disabled={busy}
              />
            </div>

            <button
              data-testid="assistant-chat-send"
              onClick={askQuestion}
              disabled={busy || !connected || !input.trim()}
              className={twMerge(
                "inline-flex h-10 items-center justify-center gap-2 rounded-[16px] px-5 text-[14px] font-semibold transition disabled:cursor-not-allowed",
                chatUi.sendButton,
                chatUi.sendButtonHover,
                chatUi.sendButtonDisabled,
              )}
              title={busy ? "Sending..." : "Send"}
            >
              <CircleSendIcon className="h-5 w-5" />
              <span>{busy ? "Sending..." : "Send"}</span>
            </button>
          </div>
          <p className={twMerge("ml-2 mt-2 text-[11px]", chatUi.helperText)}>{mergedAssistantConfig.helperText}</p>
        </footer>
      </section>

      {showFeedbackModal ? (
        <div
          className={twMerge(
            "fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm",
            chatUi.feedbackModalBackdrop,
          )}
          role="dialog"
          aria-modal="true"
          onClick={() => setShowFeedbackModal(false)}
        >
          <div
            className={twMerge(
              "w-[560px] max-w-[92vw] rounded-[28px] border px-5 pb-5 pt-6 shadow-[0_28px_70px_rgba(15,23,42,0.22)]",
              chatUi.feedbackModalPanel,
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-2 flex items-start justify-between">
              <div>
                <p className={twMerge("text-xs font-semibold uppercase tracking-[0.18em]", chatUi.feedbackTagText)}>
                  Response feedback
                </p>
                <h3 className={twMerge("mt-1 text-lg font-semibold", chatUi.feedbackTitle)}>Tell us what was off</h3>
              </div>
              <button
                type="button"
                className={twMerge(
                  "flex h-6 w-6 items-center justify-center rounded-full border transition",
                  chatUi.feedbackClose,
                  chatUi.feedbackCloseHover,
                )}
                onClick={() => setShowFeedbackModal(false)}
                aria-label="Close feedback modal"
                title="Close feedback modal"
              >
                <svg
                  className="h-3.5 w-3.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <p className={twMerge("text-sm leading-6", chatUi.feedbackBody)}>
              Select the closest reason, then add details if you want to help improve future responses.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {FEEDBACK_REASONS.map((reason) => {
                const isSelected = selectedReason === reason;
                return (
                  <button
                    key={reason}
                    type="button"
                    className={twMerge(
                      "rounded-full border px-3 py-1.5 text-[11px] font-semibold transition",
                      isSelected ? chatUi.feedbackReasonSelected : chatUi.feedbackReasonIdle,
                      !isSelected ? chatUi.feedbackReasonHover : "",
                    )}
                    onClick={() => setSelectedReason(reason)}
                  >
                    {reason}
                  </button>
                );
              })}
            </div>

            <textarea
              data-testid="assistant-chat-feedback-details"
              className={twMerge(
                "mt-4 w-full resize-none rounded-2xl border px-3 py-2.5 text-sm outline-none transition",
                chatUi.feedbackTextarea,
                chatUi.feedbackTextareaFocus,
              )}
              rows={4}
              maxLength={ASSISTANT_CHAT_LIMITS.feedbackCharacters}
              placeholder="Share details (optional)"
              value={feedbackDetails}
              onChange={(event) => setFeedbackDetails(event.target.value)}
            />
            <p className={twMerge("mt-1 text-xs", chatUi.feedbackBody)}>
              {feedbackDetails.length.toLocaleString()} / 4,000 characters
            </p>
            {err ? (
              <p role="alert" className="mt-2 text-sm">
                {err}
              </p>
            ) : null}

            <div className="mr-1 mt-4 flex justify-end">
              <button
                data-testid="assistant-chat-feedback-submit"
                type="button"
                className={twMerge(
                  "rounded-full px-5 py-2.5 text-sm font-semibold transition",
                  selectedReason
                    ? twMerge(chatUi.feedbackSubmit, chatUi.feedbackSubmitHover)
                    : twMerge("cursor-not-allowed", chatUi.feedbackSubmitDisabled),
                )}
                onClick={submitDislikeFeedback}
                disabled={!selectedReason || !connected}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { OtHullCleaningChat };
